import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoRequestPage = () => {
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    company: '',
    email: '',
    product: '',
    use_case: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/demo-requests`, formData);
      setSubmitted(true);
      toast.success('Demo request submitted successfully!');
    } catch (error) {
      console.error('Failed to submit demo request:', error);
      toast.error('Failed to submit demo request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 flex items-center justify-center py-12 px-6">
      <SEOHead page="demo" />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="demo-success-heading">
            Request Received!
          </h2>
          <p className="text-slate-600 mb-6">
            Thank you for your interest. Our team will contact you within 24 hours to schedule your personalized demo.
          </p>
          <Button onClick={() => setSubmitted(false)} data-testid="demo-submit-another-btn">
            Submit Another Request
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-slate-900 mb-4" data-testid="demo-request-heading">
            Request a Demo
          </h1>
          <p className="text-lg text-slate-600">
            See how Digian.ai can transform your enterprise. Fill out the form below and we'll be in touch.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="demo-request-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  data-testid="demo-name-input"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Company *</Label>
                <Input
                  id="company"
                  type="text"
                  placeholder="Your Company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  required
                  data-testid="demo-company-input"
                  className="h-12"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="demo-email-input"
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="product">Product of Interest *</Label>
              <Select value={formData.product} onValueChange={(value) => setFormData({ ...formData, product: value })} required>
                <SelectTrigger className="h-12" data-testid="demo-product-select">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.name}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="use_case">Tell us about your use case *</Label>
              <Textarea
                id="use_case"
                placeholder="Describe your requirements, challenges, and goals..."
                value={formData.use_case}
                onChange={(e) => setFormData({ ...formData, use_case: e.target.value })}
                required
                data-testid="demo-usecase-input"
                rows={6}
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12"
              disabled={loading}
              data-testid="demo-submit-btn"
            >
              {loading ? 'Submitting...' : 'Request Demo'}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="grid md:grid-cols-3 gap-6 text-center text-sm">
              <div>
                <div className="font-semibold text-slate-900 mb-1">24-Hour Response</div>
                <div className="text-slate-600">Quick turnaround time</div>
              </div>
              <div>
                <div className="font-semibold text-slate-900 mb-1">Personalized Demo</div>
                <div className="text-slate-600">Tailored to your needs</div>
              </div>
              <div>
                <div className="font-semibold text-slate-900 mb-1">No Commitment</div>
                <div className="text-slate-600">Free consultation</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DemoRequestPage;